// w40 Yomg'irdan so'ng (After the Rain) — night-to-day wedding preview card.
//
// The full template opens on a rain-streaked night village and, as the
// guest scrolls, the page background interpolates through dawn into a
// clear day sky. The catalog card compresses that whole journey into one
// still frame: a vertical gradient running deep navy -> plum -> rose ->
// sky blue from top to bottom, a scatter of tiny rain streaks confined to
// the top third (night), a cream ring monogram holding the couple's
// initials, the couple's names in a warm-cream serif treatment, and a
// gold rule + date line below. Uses the site's already-loaded serif stack
// (via `font`) — no new font import.
import { type LayoutCtx } from "./_shared";

// Deterministic rain streaks — same positions/lengths on every render (no
// Math.random so SSR/CSR + repeated mounts stay visually stable). Confined
// to the top third of the card, matching the "night" band of the gradient.
const RAIN: Array<{ x: number; y: number; len: number; o: number }> = [
  { x: 6, y: 4, len: 10, o: 0.5 },
  { x: 14, y: 10, len: 8, o: 0.35 },
  { x: 22, y: 2, len: 12, o: 0.45 },
  { x: 30, y: 12, len: 9, o: 0.3 },
  { x: 38, y: 6, len: 11, o: 0.4 },
  { x: 48, y: 14, len: 8, o: 0.3 },
  { x: 58, y: 3, len: 10, o: 0.45 },
  { x: 66, y: 11, len: 9, o: 0.35 },
  { x: 74, y: 5, len: 12, o: 0.4 },
  { x: 82, y: 13, len: 8, o: 0.3 },
  { x: 90, y: 7, len: 10, o: 0.4 },
  { x: 12, y: 20, len: 9, o: 0.28 },
  { x: 44, y: 22, len: 8, o: 0.25 },
  { x: 78, y: 21, len: 9, o: 0.28 },
];

// Split "Timur & Madina" / "Jasur va Gulnora" / "Дилшод и Мадина" into
// initials joined with a middot, matching the design's splash monogram.
// Falls back to a single initial when only one name is present — no
// dangling separator.
function initials(names: string): string {
  const parts = names
    .split(/\s*[&+—]\s*|\s+(?:va|и)\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const letters = parts.map((p) => p.charAt(0).toUpperCase());
  return letters.length > 1 ? `${letters[0]}·${letters[1]}` : letters[0] || "";
}

export function YomgirPreviewLayout({ names, date, accent, ink, font }: LayoutCtx) {
  const gold = accent || "#d98e3f";
  const cream = ink || "#f7ecd9";
  const mono = initials(names);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // Night -> dawn -> day, top to bottom.
        background:
          "linear-gradient(180deg, #0a1428 0%, #1c1a3a 22%, #4a2f52 42%, #b25c66 60%, #e2926a 74%, #8fb9d6 100%)",
        padding: "6%",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* Tiny rain streaks, top third only. */}
      {RAIN.map((drop, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${drop.x}%`,
            top: `${drop.y}%`,
            width: 1,
            height: drop.len,
            background: cream,
            opacity: drop.o,
            transform: "rotate(12deg)",
          }}
        />
      ))}

      <div
        style={{
          flex: 1,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${cream}33`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: cream,
            marginTop: "4%",
            marginBottom: "8%",
          }}
        >
          TO'Y TAKLIFNOMASI
        </div>

        {/* Cream ring monogram. */}
        <div
          style={{
            width: "30%",
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            marginBottom: "8%",
            display: "grid",
            placeItems: "center",
            background: "rgba(10, 20, 40, 0.28)",
            boxShadow: `inset 0 0 0 1px ${cream}, inset 0 0 0 4px transparent, inset 0 0 0 5px ${cream}44`,
          }}
        >
          <span
            style={{
              fontFamily: font,
              fontSize: 13,
              letterSpacing: "0.06em",
              color: cream,
            }}
          >
            {mono}
          </span>
        </div>

        {/* Warm-cream names. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 26,
            lineHeight: 1.15,
            letterSpacing: "0.01em",
            color: cream,
            textShadow: "0 1px 10px rgba(0,0,0,0.25)",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Gold rule. */}
        <div style={{ width: "34%", height: 1, background: `${gold}cc`, marginBottom: "6%" }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: gold,
          }}
        >
          {date.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
